import GmLayout from '@/layouts/GmLayout';

/**
 * Statistics data from the backend.
 */
interface Stats {
    totalUsers: number;
    bannedUsers: number;
    newUsersToday: number;
    newUsersThisWeek: number;
    totalHexes: number;
    hexesClaimedToday: number;
    hexesClaimedThisWeek: number;
    totalImages: number;
    imagesUploadedToday: number;
    imagesUploadedThisWeek: number;
}

interface Props {
    stats: Stats;
}

/**
 * StatCard - Displays a single statistic with label and value.
 */
function StatCard({ label, value, icon }: { label: string; value: number; icon: string }) {
    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-3">
                <span className="text-2xl">{icon}</span>
                <div>
                    <p className="text-2xl font-bold text-white">{value.toLocaleString()}</p>
                    <p className="text-sm text-gray-400">{label}</p>
                </div>
            </div>
        </div>
    );
}

/**
 * StatSection - Groups related statistics together.
 */
function StatSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4">{title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {children}
            </div>
        </div>
    );
}

/**
 * GM Dashboard - Overview page with statistics and quick links.
 */
export default function Dashboard({ stats }: Props) {
    return (
        <GmLayout title="Dashboard" activePath="/gm">
            <div className="max-w-6xl">
                <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>

                {/* Quick Actions - at the top for easy access */}
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
                    <div className="flex gap-4">
                        <a
                            href="/gm/images"
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                        >
                            🖼️ Moderate Images
                        </a>
                        <a
                            href="/gm/users"
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                        >
                            👥 Manage Users
                        </a>
                        <a
                            href="/gm/global-challenges"
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                        >
                            🌍 Global Challenges
                        </a>
                    </div>
                </div>

                {/* User Statistics */}
                <StatSection title="👥 Users">
                    <StatCard label="Total Users" value={stats.totalUsers} icon="👤" />
                    <StatCard label="Banned Users" value={stats.bannedUsers} icon="🚫" />
                    <StatCard label="New Today" value={stats.newUsersToday} icon="📅" />
                    <StatCard label="New This Week" value={stats.newUsersThisWeek} icon="📆" />
                </StatSection>

                {/* Hex Statistics */}
                <StatSection title="🗺️ Hexes">
                    <StatCard label="Total Hexes" value={stats.totalHexes} icon="⬡" />
                    <StatCard label="Claimed Today" value={stats.hexesClaimedToday} icon="📅" />
                    <StatCard label="Claimed This Week" value={stats.hexesClaimedThisWeek} icon="📆" />
                </StatSection>

                {/* Image Statistics */}
                <StatSection title="🖼️ Images">
                    <StatCard label="Total Images" value={stats.totalImages} icon="📷" />
                    <StatCard label="Uploaded Today" value={stats.imagesUploadedToday} icon="📅" />
                    <StatCard label="Uploaded This Week" value={stats.imagesUploadedThisWeek} icon="📆" />
                </StatSection>
            </div>
        </GmLayout>
    );
}
